import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { isInAppEnabled, useNotificationPrefs } from "@/lib/notification-prefs";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/_authenticated/notifications")({
  head: () => ({ meta: [{ title: "Notifications — CryptoBull Pro" }] }),
  component: NotificationsPage,
});

function NotificationsPage() {
  const qc = useQueryClient();
  const { data: prefs } = useNotificationPrefs();
  const { data: all = [] } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      const { data } = await supabase
        .from("notifications")
        .select("*")
        .order("created_at", { ascending: false });
      return data ?? [];
    },
  });
  const notifications = all.filter((n) => isInAppEnabled(prefs, n.type));

  const markAll = async () => {
    const { error } = await supabase.rpc("mark_notifications_read", { _ids: [] as string[] });
    if (error) toast.error(error.message);
    else qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Notifications</h1>
          <p className="text-sm text-muted-foreground">Account activity and platform updates.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/settings" className="text-xs text-primary hover:underline">Preferences</Link>
          {notifications.some((n) => !n.read_at) && (
            <Button variant="outline" size="sm" onClick={markAll}>Mark all as read</Button>
          )}
        </div>
      </div>
      <div className="glass rounded-2xl divide-y divide-white/5">
        {notifications.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">No notifications yet.</div>
        ) : notifications.map((n) => (
          <div key={n.id} className={`p-4 ${!n.read_at ? "bg-primary/5" : ""}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-sm font-medium">{n.title}</div>
                {n.body && <div className="text-sm text-muted-foreground mt-1">{n.body}</div>}
              </div>
              <div className="text-[11px] text-muted-foreground whitespace-nowrap">{fmtDate(n.created_at)}</div>
            </div>
            {n.link && (
              <a href={n.link} className="text-xs text-primary hover:underline mt-2 inline-block">
                Open →
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}